import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./Login.css";
import {
  Form,
  Input,
  Button,
  Typography,
  message,
  Alert,
  Spin,
  Modal,
  AutoComplete,
  Row,
  Col,
} from "antd";
import {
  MobileTwoTone,
} from "@ant-design/icons";
import "../../config";
import axios from "axios";

const { Title, Text } = Typography;

// GenerateOTP's 400s, put into the customer's words. "Can not Login" is returned when the
// number also sits in Suppliers or is an Employee with EMPCODE 'W' - both are deliberate
// blocks, not something the customer can fix by registering. Anything unrecognised falls
// through to the server's own text so a new backend message is never swallowed.
const OTP_ERROR_MESSAGES = {
  "Can not Login":
    "This number cannot be used to log in here. Please contact us for help.",
  "Not Able to Send OTP.":
    "Could not send the OTP right now. Please try again in a moment.",
  "Error Occured":
    "Something went wrong while sending the OTP. Please try again.",
};

const otpErrorText = (errMsg) =>
  OTP_ERROR_MESSAGES[errMsg] ||
  errMsg ||
  "Failed to send OTP. Please contact us for this issue.";

const Login = ({ setToken, urlParams }) => {
  const [password] = useState();
  const [mobileNumber, setMobileNumber] = useState();
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [otpErroMessage, setOtpErrorMessage] = useState("");
  const [otpSuccessMessage, setOtpSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactNo, setContactNo] = useState("");
  const [contactState, setContactState] = useState("");
  const [contactCity, setContactCity] = useState("");
  const [pincode, setPincode] = useState(""); // New state for pincode
  const [indianStates, setIndianStates] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [allCities, setAllCities] = useState([]); // Add this state to keep the full city list for the selected state
  const [showSuccessModal, setShowSuccessModal] = useState(false); // Add this state
  const [registerError, setRegisterError] = useState(""); // Add this state
  const [pincodeLoading, setPincodeLoading] = useState(false); // Add this state
  const [reactivationModalVisible, setReactivationModalVisible] =
    useState(false);
  const [reactivationInProgress, setReactivationInProgress] = useState(false);
  const [pendingModalVisible, setPendingModalVisible] = useState(false);
  const [reactivationSuccessVisible, setReactivationSuccessVisible] =
    useState(false);
  const [form] = Form.useForm();

  // Cooldown between resends. The server allows 10 OTPs per hour per user, so
  // without this a few impatient taps would burn the whole hour's allowance and
  // lock the user out of their own login.
  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = setTimeout(() => setResendSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendSeconds]);

  // Redirect to category list if already logged in
  useEffect(() => {
    console.log("=== COMPONENT MOUNT ===", urlParams);
    const token = localStorage.getItem("token");
    if (token) {
      window.location.href = "/productcategories";
    }
    document.title = "JOJ | Login";
    // Check for mn param in URL
    const params = new URLSearchParams(window.location.search);
    const mn = params.get("mn");
    if (mn && /^\d{10}$/.test(mn)) {
      console.log("mn", mn);

      setMobileNumber(mn);
      form.setFieldsValue({ mobileNumber: mn }); // <-- Add this line
    }
    // If mn is null, check urlParams for mob value
    else if (urlParams?.mob) {
      console.log("Using mob from urlParams:", urlParams.mob);

      // Clean the mobile number from urlParams
      const cleanedMob = cleanMobileNumber(urlParams.mob);

      if (cleanedMob) {
        console.log("Cleaned mob:", cleanedMob);
        setMobileNumber(cleanedMob);
        form.setFieldsValue({ mobileNumber: cleanedMob });
      }
    }

    // Fetch Indian states from API on mount
    async function fetchStates() {
      try {
        const response = await fetch(
          "https://countriesnow.space/api/v0.1/countries/states",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ country: "India" }),
          }
        );
        const data = await response.json();
        if (!data.error && data.data && Array.isArray(data.data.states)) {
          const stateNames = data.data.states.map((s) => s.name);
          setIndianStates(stateNames);
          setFilteredStates(stateNames);
        }
      } catch (err) {
        // fallback to static list if needed
        setIndianStates([
          "Maharashtra",
          "Gujarat",
          "Delhi",
          "Karnataka",
          "Tamil Nadu",
          "Uttar Pradesh",
          "West Bengal",
          "Rajasthan",
          "Madhya Pradesh",
          "Punjab",
        ]);
        setFilteredStates([
          "Maharashtra",
          "Gujarat",
          "Delhi",
          "Karnataka",
          "Tamil Nadu",
          "Uttar Pradesh",
          "West Bengal",
          "Rajasthan",
          "Madhya Pradesh",
          "Punjab",
        ]);
      }
    }
    fetchStates();
  }, [urlParams]);

  // Add the cleanMobileNumber function if not already present
  const cleanMobileNumber = (mobileNo) => {
    if (!mobileNo) return null;

    // Remove all non-digit characters
    let cleaned = mobileNo.replace(/\D/g, "");

    // Remove leading +91 or 91
    if (cleaned.startsWith("91") && cleaned.length > 10) {
      cleaned = cleaned.substring(2);
    }

    // Check if it's exactly 10 digits
    if (cleaned.length === 10) {
      return cleaned;
    }

    return null; // Invalid mobile number
  };

  const handleSendOtp = async () => {
    setLoading(true); // Show loader
    setOtpErrorMessage("");
    setOtpSuccessMessage("");
    const mobileNumberRegex = /^[0-9]{10}$/;
    if (!mobileNumber) {
      setOtpErrorMessage("Please enter your mobile number.");
      setLoading(false); // Hide loader
      return;
    }
    if (!mobileNumberRegex.test(mobileNumber)) {
      setOtpErrorMessage("Please Enter Valid Mobile Number");
      setLoading(false); // Hide loader
      return;
    }
    setLoading(true); // Show loader
    await sendOtp(mobileNumber);
    //setLoading(false); // Hide loader
  };

  const handleResendOtp = async () => {
    if (resendSeconds > 0 || loading) return;
    // Clear the boxes so the stale code cannot be submitted by mistake once the
    // new one arrives - both are valid server-side until the old one expires.
    setOtp("");
    setOtpErrorMessage("");
    setOtpSuccessMessage("");
    await sendOtp(mobileNumber);
  };

  async function loginUser(credentials) {
    const raw =
      "grant_type=password&username=" +
      credentials.username +
      "&password=" +
      credentials.password;

    const requestOptions = {
      method: "POST",
      body: raw,
    };

    try {
      return fetch(global.config.APIURL.BaseURL + "/token", requestOptions)
        .then((response) => response.json())
        .then((responseJson) => responseJson)
        .catch((error) => {});
    } catch (error) {}
  }

  async function getUserDetails() {
    const token = localStorage.getItem("token");

    try {
      let config = {
        method: "get",

        url:
          global.config.APIURL.BaseURL + "api/UserAccount/GetUserAccountDetail",
        headers: {
          Authorization: "Bearer " + token,
        },
      };

      axios
        .request(config)
        .then((response) => {
          if (response.data.IsEmployee) {
            localStorage.setItem("IsEmployee", response.data.IsEmployee);
          } else {
            localStorage.setItem("IsEmployee", false);
          }
          if (response.data.OwnerName) {
            localStorage.setItem("userName", response.data.OwnerName);
          } else {
            localStorage.setItem("userName", response.data.FirmName);
          }
        })
        .catch((error) => {});
    } catch (err) {}
  }

  const handleVerifyOtp = async () => {
    setOtpSuccessMessage("");
    if (!otp) {
      setOtpErrorMessage("Please enter the OTP.");

      return;
    }
    setLoading(true); // Show loader
    const token = await verifyOtp(mobileNumber, otp);

    // If verifyOtp returns a token, redirect appropriately
    if (token) {
      localStorage.setItem("token", token);
      setToken(token);
      message.success("Login successful!");

      // Check if we have urlParams indicating where the user came from
      if (urlParams?.fullPath && urlParams.fullPath !== "/login") {
        // Redirect to the original page with query parameters
        const redirectUrl = urlParams.fullPath + (urlParams.searchParams || "");
        console.log("Redirecting to original URL:", redirectUrl);
        window.location.href = redirectUrl;
      } else {
        // Default redirect to productcategories
        window.location.href = "/productcategories";
      }
    }
    setLoading(false); // Hide loader
  };

  async function sendOtp(mobileNumber) {
    setLoading(true); // Show loader
    try {
      let config = {
        method: "post",
        url:
          global.config.APIURL.BaseURL +
          "api/User/GenerateOTP?MobileNumber=" +
          mobileNumber,
      };

      axios
        .request(config)
        .then((response) => {
          setOtpSuccessMessage("OTP sent successfully!");
          setIsOtpSent(true);
          setResendSeconds(30);
          setLoading(false);
        })
        .catch((error) => {
          const errMsg =
            error?.response?.data?.Message ||
            error?.response?.data?.message ||
            (typeof error?.response?.data === "string"
              ? error.response.data
              : "") ||
            "";

          console.log("=== SEND OTP ERROR ===", errMsg);

          // Specific handling: deactivated account message from backend
          if (
            error?.response?.status === 400 &&
            errMsg === "Not able to Login Please Contact Us."
          ) {
            // Show reactivation modal (instead of registration/contact modal)
            setReactivationModalVisible(true);
            setContactNo(mobileNumber); // autofill mobile
            setLoading(false);
            return;
          }

          // The server's 10-per-hour cap. This user is already registered and has
          // just asked too often, so the register/contact modal would be wrong -
          // they only need to wait.
          if (
            error?.response?.status === 400 &&
            errMsg === "Reached Maximum Limit of OTP."
          ) {
            setOtpErrorMessage(
              "Too many OTP requests. Please try again after some time."
            );
            setLoading(false);
            return;
          }

          // The register modal hard-codes "This number is not registered with us", so it is
          // only right for that one case. It used to be the fallback for every remaining 400
          // - pending approval, blocked supplier/'W' employee, OTP send failure - which told
          // registered customers they had no account and invited them to register again.
          // A second registration on the same MobileNo1 leaves two AspNetUsers rows sharing
          // the number, and OTP login resolves accounts by mobile (FirstOrDefault), so that
          // silently breaks login for both.
          if (errMsg === "Mobile Number Not Found") {
            setOtpErrorMessage("");
            setShowContactModal(true); // Show modal on error
            setContactNo(mobileNumber); // Autofill contact no
            setLoading(false);
            return;
          }

          setOtpErrorMessage(otpErrorText(errMsg));
          setLoading(false);
        });
    } catch (err) {
      setOtpErrorMessage("Error sending OTP.");
      setLoading(false);
    }
  }

  async function verifyOtp(mobileNumber, otp) {
    try {
      const response = await fetch(
        global.config.APIURL.BaseURL +
          "api/User/VerifyOTP?MobileNumber=" +
          mobileNumber +
          "&OTP=" +
          otp,
        {
          method: "POST",
        }
      );
      if (response.ok) {
        const token = await loginUser({
          username: mobileNumber,
          password,
        });
        if (token && token.access_token) {
          setToken(token.access_token);
          await getUserDetails();
          return token.access_token; // Return the token string
        }
      } else {
        setOtpErrorMessage("Invalid OTP.");
      }
    } catch (error) {
      setOtpErrorMessage(error.message);
    }
    return null;
  }

  // Handlers for state/city autocomplete
  const handleStateSearch = (value) => {
    setContactState(value);
    setFilteredStates(
      indianStates.filter((state) =>
        state.toLowerCase().includes(value.toLowerCase())
      )
    );
    setFilteredCities([]); // Reset cities when state changes
    setContactCity("");
  };

  // Update handleStateSelect to set both filteredCities and allCities
  const handleStateSelect = async (value) => {
    setContactState(value);
    setContactCity("");
    setFilteredCities([]);
    setAllCities([]);
    try {
      const response = await fetch(
        "https://countriesnow.space/api/v0.1/countries/state/cities",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ country: "India", state: value }),
        }
      );
      const data = await response.json();
      if (!data.error && Array.isArray(data.data)) {
        setAllCities(data.data);
        setFilteredCities(data.data);
      } else {
        setAllCities([]);
        setFilteredCities([]);
      }
    } catch (err) {
      setAllCities([]);
      setFilteredCities([]);
    }
  };

  const handleCitySearch = (value) => {
    setContactCity(value);
    if (allCities && Array.isArray(allCities)) {
      setFilteredCities(
        allCities.filter((city) =>
          city.toLowerCase().includes(value.toLowerCase())
        )
      );
    } else {
      setFilteredCities([]);
    }
  };

  const handleCitySelect = (value) => {
    setContactCity(value);
    // Reset filteredCities to allCities so suggestions always show on next input
    setFilteredCities(allCities);
  };

  // Handler for pincode change and API call
  const handlePincodeChange = async (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only digits
    setPincode(value);

    if (value.length === 6) {
      setPincodeLoading(true); // Show loading on modal
      try {
        const response = await fetch(
          `https://api.postalpincode.in/pincode/${value}`
        );
        const data = await response.json();
        if (
          Array.isArray(data) &&
          data[0].Status === "Success" &&
          Array.isArray(data[0].PostOffice) &&
          data[0].PostOffice.length > 0
        ) {
          const post = data[0].PostOffice[0];
          // Set state and city from API
          setContactState(post.State);
          setContactCity(post.Region);
          // Optionally, update filteredStates and filteredCities for autocomplete
          if (indianStates.includes(post.State)) {
            setFilteredStates([post.State]);
          }
          setFilteredCities([post.Region]);
        }
      } catch (err) {
        // Optionally handle error
      } finally {
        setPincodeLoading(false); // Hide loading on modal
      }
    }
  };

  const handleRegister = async () => {
    // Simple validation
    setRegisterError(""); // Reset error before validation
    if (!contactName.trim()) {
      setRegisterError("Please enter your name.");
      return;
    }
    if (!contactNo.trim() || contactNo.length !== 10) {
      setRegisterError("Please enter a valid 10-digit contact number.");
      return;
    }
    if (!pincode.trim() || pincode.length !== 6) {
      setRegisterError("Please enter a valid 6-digit pincode.");
      return;
    }
    if (!contactState.trim()) {
      setRegisterError("Please select a state.");
      return;
    }
    if (!contactCity.trim()) {
      setRegisterError("Please select a city.");
      return;
    }

    const reqBody = {
      Name: contactName,
      ContactNo: contactNo,
      PinCode: pincode,
      State: contactState,
      City: contactCity,
    };

    try {
      setLoading(true);
      const response = await axios.post(
        global.config.APIURL.BaseURL + "api/PublicData/NewUserRegRequest",
        reqBody
      );
      if (response.status === 200) {
        // Reset registration form fields
        setContactName("");
        setContactNo("");
        setPincode("");
        setContactState("");
        setContactCity("");
        setFilteredStates(indianStates);
        setFilteredCities([]);
        setShowContactModal(false);
        setShowSuccessModal(true); // Show success modal
        setRegisterError(""); // Clear error

        // Reset login card fields as well
        setMobileNumber("");
        setOtpErrorMessage("");
        setOtpSuccessMessage("");
        setIsOtpSent(false);
      } else {
        setRegisterError("Failed to submit registration request.");
      }
    } catch (err) {
      // Show error from API if available
      if (err.response && err.response.data && err.response.data.Message) {
        setRegisterError(err.response.data.Message);
      } else if (err.response && err.response.data) {
        setRegisterError(
          typeof err.response.data === "string"
            ? err.response.data
            : "Bad request."
        );
      } else {
        setRegisterError("Failed to submit registration request.");
      }
    } finally {
      setLoading(false);
    }
  };

  const requestReactivation = async () => {
    setReactivationInProgress(true);
    try {
      const response = await axios.post(
        global.config.APIURL.BaseURL +
          "api/User/RequestReactivation?MobileNumber=" +
          contactNo
      );

      // On success show a confirmation modal
      if (response && (response.status === 200 || response.status === 201)) {
        setReactivationModalVisible(false);
        setReactivationSuccessVisible(true);
      } else {
        // fallback message
        message.success(
          "Reactivation request submitted. Support will contact you shortly."
        );
        setReactivationModalVisible(false);
      }
    } catch (e) {
      console.error(e);
      const errMsg =
        e?.response?.data?.Message ||
        e?.response?.data?.message ||
        (typeof e?.response?.data === "string" ? e.response.data : "") ||
        "";

      // If backend indicates a pending request, show pending modal
      if (
        e?.response?.status === 400 &&
        errMsg ===
          "Your Request is Already with Us Please Wait while our team Review it."
      ) {
        setReactivationModalVisible(false);
        setPendingModalVisible(true);
      } else {
        message.error(
          "Failed to submit reactivation request. Please contact support."
        );
      }
    } finally {
      setReactivationInProgress(false);
    }
  };

  return (
    <Spin spinning={loading} tip="Processing...">
      <div
        style={{
          minHeight: "100vh", // Full height for the page
          display: "flex",
          justifyContent: "center",
          alignItems: "center", // Center the card vertically and horizontally
          background: "linear-gradient(135deg, #e0f7fa 0%, #e8eaf6 100%)", // Light gradient background
          padding: "16px", // Add padding for smaller screens
        }}
      >
        <div
          style={{
            maxWidth: 400,
            width: "100%", // Ensure responsiveness
            margin: "auto",
            padding: 30,
            background: "rgba(255, 255, 255, 0.95)",
            borderRadius: 16,
            boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          {/* Logo Image */}
          <div style={{ marginBottom: 20 }}>
            <img
              src="https://thejoj.com/images/jojlogo.png" // Replace with the actual path to your logo
              alt="JOJ Logo"
              style={{ width: 300, height: "auto" }}
            />
          </div>

          <Title
            level={3}
            style={{
              color: "#333",
              fontSize: window.innerWidth <= 768 ? "24px" : "32px", // Larger font size for mobile
            }}
          >
            Sign in for JOJ Catalogue
          </Title>
          <Text
            style={{
              color: "#555",
              fontSize: window.innerWidth <= 768 ? "16px" : "18px", // Adjust font size for mobile
            }}
          >
            Enter Mobile Number to access our Catalogue
          </Text>

          <Form
            form={form}
            layout="vertical"
            style={{ textAlign: "left", marginTop: 24 }}
          >
            <Form.Item
              name="mobileNumber"
              rules={[
                { required: true, message: "Please enter your mobile number" },
              ]}
            >
              <Input
                prefix={<MobileTwoTone />}
                placeholder="Mobile Number"
                size="large"
                value={mobileNumber || ""}
                onChange={(e) => setMobileNumber(e.target.value)}
                disabled={isOtpSent}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </Form.Item>
            <center>
              {isOtpSent && (
                <Form.Item
                  name="otp"
                  rules={[{ required: true, message: "Please enter the OTP" }]}
                >
                  <Text
                    style={{
                      color: "darkgray",
                      fontSize: window.innerWidth <= 768 ? "14px" : "16px", // Adjust font size for mobile
                    }}
                  >
                    Enter OTP
                  </Text>
                  <br />
                  <Input.OTP
                    autoFocus
                    style={{ marginTop: 10 }}
                    length={4}
                    value={otp}
                    onChange={(value) => setOtp(value)}
                    size="large"
                    inputMode="numeric" // <-- Add this line
                    pattern="[0-9]*" // <-- Add this line
                    type="tel" // <-- Add this line
                  />
                </Form.Item>
              )}
            </center>
            {otpErroMessage ? (
              <div style={{ textAlign: "left", marginBottom: 16 }}>
                <Alert message={otpErroMessage} type="error" />
              </div>
            ) : null}

            {otpSuccessMessage ? (
              <div style={{ textAlign: "left", marginBottom: 16 }}>
                <Alert message={otpSuccessMessage} type="success" />
              </div>
            ) : null}

            {!isOtpSent ? (
              <Button
                type="primary"
                block
                size="large"
                onClick={handleSendOtp}
                loading={loading}
              >
                Send OTP
              </Button>
            ) : (
              <>
                <Button
                  type="primary"
                  block
                  size="large"
                  onClick={handleVerifyOtp}
                >
                  Verify OTP
                </Button>
                <div style={{ textAlign: "center", marginTop: 12 }}>
                  {resendSeconds > 0 ? (
                    <Text style={{ color: "darkgray", fontSize: 14 }}>
                      Didn&apos;t get the code? Resend in {resendSeconds}s
                    </Text>
                  ) : (
                    <Button
                      type="link"
                      style={{ padding: 0, fontSize: 14 }}
                      onClick={handleResendOtp}
                      loading={loading}
                    >
                      Didn&apos;t get the code? Resend OTP
                    </Button>
                  )}
                </div>
              </>
            )}
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <Button
                type="link"
                style={{
                  color: "#1677ff",
                  fontWeight: 600,
                  fontSize: 16,
                  padding: 0,
                }}
                onClick={() => (window.location.href = "/productcategories")}
              >
                Explore Without Login
              </Button>
            </div>
          </Form>
        </div>
      </div>
      {/* Contact Modal for OTP failure */}
      <Modal
        open={showContactModal}
        onCancel={() => setShowContactModal(false)}
        footer={null}
        centered
        title={
          <div>
            <div style={{ fontWeight: 600, color: "#d4380d", marginBottom: 8 }}>
              This number is not registered with us. Register Now to access
              Catalogue
            </div>
            <div>Contact Us</div>
          </div>
        }
      >
        <Spin spinning={pincodeLoading}>
          <Form layout="vertical">
            <Form.Item label="Name" required>
              <Input
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Enter your name"
              />
            </Form.Item>
            <Form.Item label="Contact No." required>
              <Input value={contactNo} disabled placeholder="Contact Number" />
            </Form.Item>
            <Form.Item label="Pincode" required>
              <Input
                value={pincode}
                maxLength={6}
                onChange={handlePincodeChange}
                placeholder="Enter 6-digit Pincode"
                type="tel" // <-- Add this line
                inputMode="numeric" // <-- Add this line
                pattern="[0-9]*" // <-- Add this line
              />
            </Form.Item>
            <Form.Item label="State" required>
              <AutoComplete
                options={filteredStates.map((state) => ({
                  value: state,
                }))}
                value={contactState}
                onSelect={handleStateSelect}
                onSearch={handleStateSearch}
                placeholder="Select State"
                filterOption={false}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item label="City" required>
              <AutoComplete
                options={filteredCities.map((city) => ({
                  value: city,
                }))}
                value={contactCity}
                onSelect={handleCitySelect}
                onSearch={handleCitySearch}
                placeholder="Select City"
                filterOption={false}
                style={{ width: "100%" }}
                disabled={!contactState}
              />
            </Form.Item>
            {registerError && (
              <div style={{ marginBottom: 12 }}>
                <Alert message={registerError} type="error" showIcon />
              </div>
            )}
            <Row gutter={12} style={{ marginTop: 8 }}>
              <Col span={12}>
                <Button
                  type="primary"
                  block
                  onClick={handleRegister}
                  loading={loading}
                >
                  Register
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  type="primary"
                  block
                  onClick={() => setShowContactModal(false)}
                >
                  Close
                </Button>
              </Col>
            </Row>
          </Form>
        </Spin>
      </Modal>
      {/* Success Modal after registration */}
      <Modal
        open={showSuccessModal}
        onCancel={() => setShowSuccessModal(false)}
        footer={[
          <Button
            key="ok"
            type="primary"
            block
            onClick={() => setShowSuccessModal(false)}
          >
            OK
          </Button>,
        ]}
        centered
        title={
          <div style={{ fontWeight: 600, color: "#389e0d", marginBottom: 8 }}>
            Registration Successful!
          </div>
        }
      >
        <div style={{ fontSize: 16, color: "#333", textAlign: "center" }}>
          Thank you for registering with us.
          <br />
          <br />
          We have received your request. Once you receive the confirmation, you
          can try logging in to access the catalogue.
          <br />
        </div>
      </Modal>
      {/* Reactivation Modal for deactivated accounts */}
      <Modal
        open={reactivationModalVisible}
        onCancel={() => setReactivationModalVisible(false)}
        footer={[
          <Button
            key="request"
            type="primary"
            loading={reactivationInProgress}
            onClick={requestReactivation}
          >
            Request Reactivation
          </Button>,
        ]}
        centered
        title="Account Deactivated"
      >
        <p>Your account is Deactivated Due to No Login for last few days.</p>
        <p style={{ marginTop: 8, color: "rgba(0,0,0,0.65)" }}>
          Click "Request Reactivation" and our support team will review and
          contact you.
        </p>
      </Modal>

      {/* Reactivation Success Modal */}
      <Modal
        open={reactivationSuccessVisible}
        onCancel={() => setReactivationSuccessVisible(false)}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => setReactivationSuccessVisible(false)}
          >
            OK
          </Button>,
        ]}
        centered
        title="Reactivation Requested"
      >
        <p>Your reactivation request has been submitted successfully.</p>
        <p style={{ marginTop: 8, color: "rgba(0,0,0,0.65)" }}>
          Our support team will review your request and contact you shortly.
        </p>
      </Modal>

      {/* Pending Request Modal */}
      <Modal
        open={pendingModalVisible}
        onCancel={() => setPendingModalVisible(false)}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => setPendingModalVisible(false)}
          >
            OK
          </Button>,
        ]}
        centered
        title="Request Pending"
      >
        <p>
          Your Request is already with us. Please wait while our team reviews
          it.
        </p>
      </Modal>
    </Spin>
  );
};

Login.propTypes = {
  setToken: PropTypes.func.isRequired,
};

export default Login;
